import { LegalPage } from "./Legal.js";

/**
 * Terms of service.
 *
 * Deliberately short and readable. Google's verification asks for a homepage
 * and a privacy policy; terms are not strictly required, but a reviewer - and
 * an IT department deciding whether to allow the app - expects them, and the
 * limits of what the product promises are worth stating plainly while it is
 * still free and unfinished.
 */
export function Terms() {
  return (
    <LegalPage title="Terms" updated="14 September 2026">
      <p className="lede">
        The short version: Meet With shows people you invite when you&rsquo;re free.
        It is free while it is being built, it reads your calendars and never writes to
        them, and you should not rely on it alone for anything that matters.
      </p>

      <h2>Who these are between</h2>
      <p>
        You and <strong>[legal entity name]</strong>, of{" "}
        <strong>[registered address]</strong>, which operates Meet With. Using the
        service means you accept these terms. Questions:{" "}
        <a href="mailto:hello@meetwith.org">hello@meetwith.org</a>.
      </p>

      <h2>Your account</h2>
      <p>
        You sign in with a Microsoft work or school account, or with Google. Personal
        Microsoft accounts are not accepted. You are responsible for what happens under
        your account and for having the right to connect the calendars you connect — if
        the calendar belongs to an employer, that is between you and them, and your
        administrator may be asked to approve access.
      </p>

      <h2>What the service does and does not do</h2>
      <ul>
        <li>
          It reads your calendars to work out free time. It never creates, edits, moves
          or deletes an event, and never sends mail from your mailbox.
        </li>
        <li>
          Accepting a request emails both parties and attaches a calendar file. Adding
          it to a calendar is something each of you does.
        </li>
        <li>
          A movable-meeting suggestion is advice. Nothing is rearranged for you.
        </li>
        <li>
          Availability is computed from the last successful read of your calendars. If
          a provider is slow or unreachable, what you see may be out of date.
        </li>
      </ul>

      <h2>Invitations</h2>
      <p>
        Access is per email address, and there is no public link. Invite only people you
        mean to give this to: an invited visitor can see the pattern of your free time
        within your booking window, which says more about you than it might first
        appear. You can revoke an invitation at any time and it takes effect
        immediately.
      </p>

      <h2>Acceptable use</h2>
      <p>
        Do not use Meet With to harass anyone, to send unsolicited mail, to gain access
        to a calendar you have no right to, to probe or disrupt the service, or to
        break the law. We may suspend an account that does, and we may remove a
        calendar subscription whose source we cannot safely fetch.
      </p>

      <h2>While it is free</h2>
      <p>
        Meet With currently costs nothing, and there are no availability guarantees,
        no uptime commitment and no support promise. If that changes we will say so
        before it applies to you. We may change or withdraw features, and we may end
        the service — with reasonable notice and an opportunity to get your data out.
      </p>

      <h2>Liability</h2>
      <p>
        The service is provided as it is. To the extent the law allows, we are not
        liable for meetings that were missed, double-booked or wrongly scheduled, for
        lost business or data, or for anything a calendar provider does or fails to do.
        Nothing here limits liability that cannot be limited by law.
      </p>

      <h2>Ending it</h2>
      <p>
        You can stop at any time: disconnect your calendars, and ask us to delete your
        account as described in the <a href="/privacy">privacy policy</a>. We can end an
        account that breaks these terms.
      </p>

      <h2>Changes</h2>
      <p>
        If these terms change materially we will update this page and the date at the
        top. Continuing to use Meet With after that means the new version applies.
      </p>
    </LegalPage>
  );
}
